import { assertNotNull } from '@drifti-shared/utils';
import { useParams } from 'react-router-dom';

const FORBIDDEN_PARAM_NAME = 'id';

/** Parses string as int. Throws on NaN */
const toNumber = (value: string) => {
  const num = Number.parseInt(value, 10);
  if (Number.isNaN(num)) throw new Error(`Expected number, got ${value}`);
  return num;
};

/**
 * A bit of a complex type here to restrict the return type of the function based on the param name.
 * Do not use id as param name, use a more descriptive name like jobId
 */
type UseParamFn<TReturnAllowedParamName extends string | number | undefined> = <
  TParam extends string,
  TReturn = TParam extends typeof FORBIDDEN_PARAM_NAME ? never : TReturnAllowedParamName,
>(
  param: TParam
) => TReturn;

export const useTryParam: UseParamFn<string | undefined> = (param) => {
  const params = useParams();

  if (param === FORBIDDEN_PARAM_NAME) {
    throw new Error('Do not use id as param name, use a more descriptive name like jobId');
  }

  return params[param] as any;
};

export const useParam: UseParamFn<string> = (param) =>
  assertNotNull(useTryParam(param), `Missing param ${param} in url`);

export const useTryIntParam: UseParamFn<number | undefined> = (param) => {
  const value = useTryParam(param);
  return value === undefined ? undefined : (toNumber(value) as any);
};

export const useIntParam: UseParamFn<number> = (param) => toNumber(useParam(param)) as any;
